import React, {useEffect, useRef} from "react";
import {useFormContext} from "react-hook-form";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import "@styles/rich-text-editor.css";
import {LexicalComposer} from "@lexical/react/LexicalComposer";
import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin";
import {ContentEditable} from "@lexical/react/LexicalContentEditable";
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin";
import {LinkPlugin} from "@lexical/react/LexicalLinkPlugin";
import {ListPlugin} from "@lexical/react/LexicalListPlugin";
import ImagePlugin from "./plugins/image-plugin";
import RichTextToolbar from "./rich-text-toolbar";
import {LexicalErrorBoundary} from "@lexical/react/LexicalErrorBoundary";
import {LinkNode} from "@lexical/link";
import {ListItemNode, ListNode} from "@lexical/list";
import {HeadingNode, QuoteNode} from "@lexical/rich-text";
import {useToolbarVisibility} from "@common/components/forms/rich-text-form-field/hooks/use-toolbar-visibility.hook";
import FocusBlurPlugin from "@common/components/forms/rich-text-form-field/plugins/focus-blur-plugin";
import {classNames} from "@utils/class-names";
import AutoResizePlugin from "@common/components/forms/rich-text-form-field/plugins/auto-resize-plugin";
import {DashListNode} from "@common/components/forms/rich-text-form-field/nodes/dash-list-node";
import DashListPlugin from "@common/components/forms/rich-text-form-field/plugins/dash-list-plugin";
import {ImageNode} from "@common/components/forms/rich-text-form-field/nodes/image-node";
import {LoadingImageNode} from "@common/components/forms/rich-text-form-field/nodes/loading-image-node";
import FormSyncPlugin from "@common/components/forms/rich-text-form-field/plugins/form-sync-plugin";
import ImageUploadPlugin from "@common/components/forms/rich-text-form-field/plugins/image-upload-plugin";
import ToolbarPlugin, {ToolbarActions} from "@common/components/forms/rich-text-form-field/plugins/toolbar-plugin";
import PlainTextPastePlugin from "@common/components/forms/rich-text-form-field/plugins/plain-text-paste-plugin";
import DuplicateLinePlugin from "@common/components/forms/rich-text-form-field/plugins/duplicate-line-plugin";
import {ArticleFileManager} from "../../../../articles/helpers/article-file-manager";
import {EditorState, CLEAR_HISTORY_COMMAND} from "lexical";
import {TabIndentationPlugin} from "@lexical/react/LexicalTabIndentationPlugin";
import {RichTextTool} from "@common/components/forms/rich-text-form-field/tools/tools-types.enum";
import CharCountPlugin from "@common/components/forms/rich-text-form-field/plugins/char-count-plugin";

interface Props {
    name: string;
    placeholder?: string;
    isRequired?: boolean;
    classNamePlaceholder?: string;
    hiddenOptions?: Array<RichTextTool>;
    maxLength?: number;

}

const BlurHandler: React.FC<{
    onFocus: () => void;
    onBlur: () => void;
    onChange: (editorState: EditorState) => void;
    currentValue: string | null
}> = ({onFocus, onBlur, onChange, currentValue}) => {
    const [editor] = useLexicalComposerContext();
    const previousValueRef = useRef<string | null>(currentValue);
    const hasInitializedRef = useRef(false);

    // Wyczyść historię po załadowaniu początkowego contentu,
    // żeby undo nie mogło cofnąć poniżej stanu początkowego
    useEffect(() => {
        if (!hasInitializedRef.current && previousValueRef.current) {
            hasInitializedRef.current = true;

            const timer = setTimeout(() => {
                editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [editor, currentValue]);

    const handleBlur = () => {
        onBlur();

        // Synchronizuj pliki na blur
        editor.getEditorState().read(() => {
            const json = JSON.stringify(editor.getEditorState());
            ArticleFileManager.syncUsedFilesFromContent(json);

            // Sprawdź czy wartość się zmieniła
            if (json !== previousValueRef.current) {
                previousValueRef.current = json;
                onChange(editor.getEditorState());
            }
        });
    };

    // Aktualizuj ref gdy zmienia się currentValue
    useEffect(() => {
        previousValueRef.current = currentValue;
    }, [currentValue]);

    // Dodaj onChange listener tylko na blur
    useEffect(() => {
        return editor.registerUpdateListener(() => {
            // Nie wywołuj onChange przy każdej zmianie - tylko na blur
        });
    }, [editor]);

    return <FocusBlurPlugin onFocus={onFocus} onBlur={handleBlur}/>;
};

const RichTextFormField: React.FC<Props> = (props) => {
    const {
        name,
        classNamePlaceholder,
        placeholder = "Wpisz treść...",
        hiddenOptions = [],
        maxLength
    } = props;
    const {register, setValue, getValues, formState: {errors}} = useFormContext();
    const toolbarId = `rich-text-toolbar-${name}`;
    const [isToolbarActive, setToolbarActive] = useToolbarVisibility(toolbarId);

    useEffect(() => {
        register(name, {required: false});
    }, [register, name]);

    const initialConfig = {
        namespace: "RichTextEditor",
        editable: true,
        nodes: [
            ImageNode,
            LoadingImageNode,
            LinkNode,
            ListNode,
            ListItemNode,
            HeadingNode,
            QuoteNode,
            DashListNode,
        ],
        theme: {
            paragraph: "rich-text-p",
            heading: {
                h1: "rich-text-h1",
                h2: "rich-text-h2",
            },
            text: {
                bold: "rich-text-bold",
                italic: "rich-text-italic",
                underline: "rich-text-underline",
            },
            list: {
                nested: {
                    listitem: "rich-text-li",
                },
                ol: "rich-text-ol",
                ul: "rich-text-ul",
                listitem: "rich-text-li",
            },
        },
        onError(error: Error) {
            console.error(error);
        },

        editorState: (editor: { parseEditorState: (arg0: any) => any; }) => {
            const value = getValues(name);
            if (value) {
                try {
                    return editor.parseEditorState(value);
                } catch (e) {
                    console.warn("⚠️ Błędny format stanu edytora:", e);
                }
            }
            return undefined;
        },
    };

    const handleChange = (editorState: EditorState) => {
        editorState.read(() => {
            const json = JSON.stringify(editorState);
            setValue(name, json, {shouldDirty: true});
            ArticleFileManager.syncUsedFilesFromContent(json)
        });
    };

    const handleFocus = () => setToolbarActive(true);
    const handleBlur = () => setToolbarActive(false);

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="relative w-full" data-rich-text-wrapper>
                <ToolbarPlugin
                    renderToolbar={(props: ToolbarActions) => (
                        <RichTextToolbar {...props} isActiveToolbar={isToolbarActive} hiddenOptions={hiddenOptions} />
                    )}
                />

                <div
                    data-rich-text-container
                    className={classNames(
                        "rounded-lg transition-shadow duration-150 relative",
                        isToolbarActive
                            ? "before:absolute before:inset-[-8px] before:rounded-[12px] before:shadow-[0_0_0_2px_rgba(0,0,0,0.05)] before:content-[''] before:pointer-events-none"
                            : "shadow-none"
                    )}
                >
                    <RichTextPlugin
                        contentEditable={<ContentEditable className="rich-text-editor focus:outline-none text-[14px]"/>}
                        placeholder={
                            <div
                                className={classNames(
                                    "absolute top-0 left-0 pointer-events-none text-on-surface/60",
                                    classNamePlaceholder
                                )}
                            >
                                {placeholder}
                            </div>
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </div>
                {errors[name] && <p className="text-red-500 text-xs pt-2">{'' + errors[name]?.message}</p>}
                <CharCountPlugin maxLength={maxLength}/>

                <FormSyncPlugin value={getValues(name)}/>
                <BlurHandler onFocus={handleFocus} onBlur={handleBlur} onChange={handleChange}
                             currentValue={getValues(name)}/>
                <HistoryPlugin/>
                <PlainTextPastePlugin/>
                <AutoResizePlugin minHeight={50}/>
                <DuplicateLinePlugin/>
                <TabIndentationPlugin/>

                <LinkPlugin/>
                <ImagePlugin/>
                <ImageUploadPlugin/>
                <ListPlugin/>
                <DashListPlugin/>
            </div>
        </LexicalComposer>
    );
};

export default RichTextFormField;
